// src/App.js
import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import './App.css';

function App() {
  // --- 登录状态管理 ---
  // 从环境变量读取密码，如果没有配置，默认密码是 "admin888"
  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'admin888';
  // 检查本地缓存是否已经登录过
  const [isAuthenticated, setIsAuthenticated] = useState(localStorage.getItem('xz_auth') === 'true');
  const [loginPwd, setLoginPwd] = useState('');

  // --- 业务状态管理 ---
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [remark, setRemark] = useState(''); 
  const [accessKey, setAccessKey] = useState('');

  // --- 弹窗状态管理 ---
  const [editModal, setEditModal] = useState({ isOpen: false, id: null, title: '', key: '' });

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginPwd === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem('xz_auth', 'true'); // 记住登录状态
    } else {
      alert('密码错误！');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('xz_auth');
  };

  // 修改：生成以 xz_ 开头，总长度约20位的随机密钥
  const generateRandomKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = 'xz_'; // 前缀 3个字符
    // 再生成 17 个随机字符，凑够 20 个字符
    for (let i = 0; i < 17; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setAccessKey(result);
  };

  // 新增：点击复制功能
  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      alert(`✅ 复制成功: ${text}`);
    }).catch(err => {
      alert('❌ 复制失败，请手动选择复制');
    });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('xiazai')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) setDataList(data);
    } catch (error) {
      console.error('获取数据失败:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!accessKey) return alert('请先生成或输入一个密钥！');

    const { error } = await supabase
      .from('xiazai')
      .insert([{ title: remark || '未命名用户', access_key: accessKey, is_active: true }]);

    if (error) {
      alert('添加失败: ' + error.message);
    } else {
      setRemark(''); setAccessKey(''); fetchData(); 
    }
  };

  // 打开修改弹窗
  const openEditModal = (item) => {
    setEditModal({ isOpen: true, id: item.id, title: item.title, key: item.access_key });
  };

  // 提交修改
  const saveEdit = async () => {
    const { error } = await supabase
      .from('xiazai')
      .update({ title: editModal.title, access_key: editModal.key })
      .eq('id', editModal.id);

    if (error) {
      alert('修改失败: ' + error.message);
    } else {
      setEditModal({ isOpen: false, id: null, title: '', key: '' });
      fetchData(); 
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const { error } = await supabase.from('xiazai').update({ is_active: !currentStatus }).eq('id', id);
    if (error) alert('更新状态失败: ' + error.message);
    else fetchData();
  };

  // 新增：解绑设备功能
  const handleUnbind = async (id, keyName) => {
    if (!window.confirm(`确定要解绑密钥 [${keyName}] 的设备吗？\n解绑后客户可以在新电脑上重新激活！`)) return;
    
    // 将该记录的 device_id 设为空
    const { error } = await supabase.from('xiazai').update({ device_id: null }).eq('id', id);
    
    if (error) {
      alert('解绑失败: ' + error.message);
    } else {
      alert('✅ 解绑成功！该密钥已可用于新设备。');
      fetchData();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要彻底删除这个密钥吗？这会让该用户立刻掉线！')) return;
    const { error } = await supabase.from('xiazai').delete().eq('id', id);
    if (error) alert('删除失败: ' + error.message);
    else fetchData(); 
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      const subscription = supabase
        .channel('xiazai-key-manager')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'xiazai' }, () => fetchData())
        .subscribe();
      return () => supabase.removeChannel(subscription);
    }
  }, [isAuthenticated]);

  // ================= 登录页面渲染 =================
  if (!isAuthenticated) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h2>虾仔中台系统</h2>
          <form onSubmit={handleLogin}>
            <input 
              type="password" 
              className="login-input" 
              placeholder="请输入管理员密码" 
              value={loginPwd}
              onChange={(e) => setLoginPwd(e.target.value)}
              required
            />
            <button type="submit" className="btn-primary">安全登录</button>
          </form>
        </div>
      </div>
    );
  }

  // ================= 核心后台渲染 =================
  return (
    <div className="dashboard">
      <div className="header">
        <h2>🦐 虾仔 - 权限密钥分发控制台</h2>
        <button onClick={handleLogout} className="logout-btn">退出登录</button>
      </div>
      
      {/* 发放区域 */}
      <form onSubmit={handleAdd} className="control-panel">
        <input 
          type="text" className="input-field" placeholder="用途/备注 (如：张三的电脑)" 
          value={remark} onChange={(e) => setRemark(e.target.value)}
        />
        <input 
          type="text" className="input-field input-key" placeholder="手动输入密钥或点击生成" 
          value={accessKey} onChange={(e) => setAccessKey(e.target.value)}
        />
        <button type="button" onClick={generateRandomKey} className="btn-action btn-generate">
          🎲 随机生成
        </button>
        <button type="submit" className="btn-action btn-submit">
          ➕ 确认发放
        </button>
      </form>

      {/* 列表区域 */}
      <div className="table-container">
        {loading && dataList.length === 0 ? <p style={{padding: '20px', textAlign: 'center'}}>正在同步云端数据...</p> : (
          <table>
            <thead>
              <tr>
                <th>备注 / 用途</th>
                <th>授权密钥 (点击复制)</th>
                <th>设备绑定状态</th>
                <th>账号状态</th>
                <th>创建时间</th>
                <th style={{textAlign: 'center'}}>管理操作</th>
              </tr>
            </thead>
            <tbody>
              {dataList.map((item) => (
                <tr key={item.id} style={{ opacity: item.is_active ? 1 : 0.6 }}>
                  <td data-label="备注/用途"><strong>{item.title}</strong></td>
                  
                  {/* 修改：变成可点击复制的样式 */}
                  <td 
                    data-label="授权密钥" 
                    className="key-text" 
                    onClick={() => copyToClipboard(item.access_key)}
                    style={{ cursor: 'pointer', color: '#3b82f6', textDecoration: 'underline' }}
                    title="点击复制到剪贴板"
                  >
                    {item.access_key || '未分配'} 📋
                  </td>
                  
                  {/* 新增：设备绑定状态 */}
                  <td data-label="设备状态">
                    {item.device_id ? (
                      <span style={{color: '#d97706', fontWeight: 'bold'}}>🔒 已绑定</span>
                    ) : (
                      <span style={{color: '#16a34a', fontWeight: 'bold'}}>🟢 未绑定</span>
                    )}
                  </td>

                  <td data-label="当前状态">
                    <span className={`status-badge ${item.is_active ? 'status-active' : 'status-banned'}`}>
                      {item.is_active ? '🟢 正常运行' : '🔴 已封禁'}
                    </span>
                  </td>
                  <td data-label="创建时间" style={{color: '#64748b', fontSize: '13px'}}>
                    {new Date(item.created_at).toLocaleString()}
                  </td>
                  <td data-label="操作" className="action-buttons" style={{justifyContent: 'center', flexWrap: 'wrap', gap: '4px'}}>
                    <button onClick={() => openEditModal(item)} className="btn-sm btn-edit">修改</button>
                    <button onClick={() => handleToggleStatus(item.id, item.is_active)} className={`btn-sm btn-toggle ${!item.is_active ? 'banned' : ''}`}>
                      {item.is_active ? '封禁' : '解封'}
                    </button>
                    {/* 新增：解绑按钮 */}
                    <button 
                      onClick={() => handleUnbind(item.id, item.access_key)} 
                      disabled={!item.device_id}
                      style={{
                        backgroundColor: item.device_id ? '#f59e0b' : '#d1d5db',
                        color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', 
                        cursor: item.device_id ? 'pointer' : 'not-allowed', fontSize: '12px'
                      }}
                    >
                      解绑设备
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="btn-sm btn-delete">删除</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 自定义修改弹窗 */}
      {editModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>编辑密钥信息</h3>
            <label>用途 / 备注</label>
            <input 
              type="text" className="input-field" 
              value={editModal.title} 
              onChange={(e) => setEditModal({...editModal, title: e.target.value})}
            />
            <label>授权密钥 (Access Key)</label>
            <input 
              type="text" className="input-field input-key" 
              value={editModal.key} 
              onChange={(e) => setEditModal({...editModal, key: e.target.value})}
            />
            <div className="modal-actions">
              <button onClick={() => setEditModal({...editModal, isOpen: false})} className="btn-cancel">取消</button>
              <button onClick={saveEdit} className="btn-action btn-submit">保存修改</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;